import { useCallback, useEffect, useRef, useState } from "react";
import editingLockService from "../services/editingLock";

const HEARTBEAT_INTERVAL_MS = 30_000; // 30s

/**
 * Hook que gerencia o lock cooperativo de edição (Fase 2 do controle de concorrência).
 *
 * Ao montar com `entityName`/`entityId` válidos, tenta adquirir o lock.
 * - Se sucesso: dispara heartbeat periódico (30s) e libera ao desmontar.
 * - Se 423 (Locked): expõe `isLocked=true` com dados do dono atual.
 *
 * @param {string} entityName Nome da entidade (ex.: "SolicitacaoDeLicenca").
 * @param {string} entityId   GUID do recurso a bloquear.
 * @param {boolean} enabled   Se false, não tenta adquirir lock (modo apenas-leitura).
 */
export default function useEditingLock(entityName, entityId, enabled = true) {
  const [state, setState] = useState({
    loading: enabled,
    isOwner: false,
    isLocked: false, // true => travado por OUTRO usuário
    lockedBy: null, // { id, nome }
    lockedSince: null,
    error: null,
  });

  const heartbeatRef = useRef(null);
  const releasedRef = useRef(false);

  const stopHeartbeat = useCallback(() => {
    if (heartbeatRef.current) {
      clearInterval(heartbeatRef.current);
      heartbeatRef.current = null;
    }
  }, []);

  const startHeartbeat = useCallback(() => {
    stopHeartbeat();
    heartbeatRef.current = setInterval(async () => {
      try {
        await editingLockService.heartbeat(entityName, entityId);
      } catch {
        // Heartbeat falhou — silencioso, próxima tentativa pode recuperar.
      }
    }, HEARTBEAT_INTERVAL_MS);
  }, [entityName, entityId, stopHeartbeat]);

  const tryAcquire = useCallback(async () => {
    if (!enabled || !entityName || !entityId) {
      setState((s) => ({ ...s, loading: false }));
      return;
    }
    try {
      const data = await editingLockService.acquire(entityName, entityId);
      setState({
        loading: false,
        isOwner: true,
        isLocked: false,
        lockedBy: { id: data.usuarioId, nome: data.usuarioNome },
        lockedSince: data.lockedAt,
        error: null,
      });
      startHeartbeat();
    } catch (err) {
      const status = err?.response?.status;
      const data = err?.response?.data;
      if (status === 423 && data?.type === "resource_locked") {
        setState({
          loading: false,
          isOwner: false,
          isLocked: true,
          lockedBy: { id: data.lockOwnerId, nome: data.lockOwnerNome },
          lockedSince: data.lockedAt,
          error: null,
        });
      } else {
        setState((s) => ({ ...s, loading: false, error: err }));
      }
    }
  }, [enabled, entityName, entityId, startHeartbeat]);

  const forceUnlock = useCallback(async () => {
    try {
      const data = await editingLockService.forceRelease(entityName, entityId);
      setState({
        loading: false,
        isOwner: true,
        isLocked: false,
        lockedBy: { id: data.usuarioId, nome: data.usuarioNome },
        lockedSince: data.lockedAt,
        error: null,
      });
      startHeartbeat();
    } catch (err) {
      setState((s) => ({ ...s, error: err }));
    }
  }, [entityName, entityId, startHeartbeat]);

  useEffect(() => {
    releasedRef.current = false;
    tryAcquire();

    return () => {
      stopHeartbeat();
      // Tenta liberar best-effort ao desmontar (não bloqueia).
      if (!releasedRef.current && enabled && entityName && entityId) {
        releasedRef.current = true;
        editingLockService.release(entityName, entityId).catch(() => {});
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entityName, entityId, enabled]);

  return {
    ...state,
    forceUnlock,
  };
}
